// Step 5 of the client-side X12 pipeline (843 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — header, Name loops, PO1-style
// quoted-item loops (with a nested Name sub-loop), and the summary. Only
// ever runs against a transaction whose ST01 is "843"; anything else is
// left alone so it can still be shown, read-only, by the UI. Mirrors
// parser840.ts's pattern one-for-one — the 843 is 840's reply, with BQR
// (Beginning Segment for Response to Request for Quotation) in place of
// BQT and no header TAX segment. parser840.ts itself is never touched by
// this file.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header843 {
  bqr?: EdiSegment;
  cur?: EdiSegment;
  refs: EdiSegment[];
  per?: EdiSegment;
  fob?: EdiSegment;
  itd?: EdiSegment;
  dtm: EdiSegment[];
  msg: EdiSegment[];
  other: EdiSegment[];
}

export interface NameLoop843 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemNameSubLoop843 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemLoop843 {
  po1: EdiSegment;
  pid: EdiSegment[];
  mea: EdiSegment[];
  ctp?: EdiSegment;
  sac: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  sdq: EdiSegment[];
  dtm: EdiSegment[];
  subNameLoops: ItemNameSubLoop843[];
  other: EdiSegment[];
}

export interface Summary843 {
  ctt?: EdiSegment;
  amt: EdiSegment[];
}

export interface Parsed843 {
  header: Header843;
  nameLoops: NameLoop843[];
  items: ItemLoop843[];
  summary: Summary843;
}

function attachHeaderSegment(header: Header843, s: EdiSegment): void {
  switch (s.id) {
    case 'BQR':
      header.bqr = s;
      break;
    case 'CUR':
      header.cur = s;
      break;
    case 'REF':
      header.refs.push(s);
      break;
    case 'PER':
      header.per = s;
      break;
    case 'FOB':
      header.fob = s;
      break;
    case 'ITD':
      header.itd = s;
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    case 'MSG':
      header.msg.push(s);
      break;
    default:
      header.other.push(s);
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop843, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemNameSubLoopSegment(
  loop: ItemNameSubLoop843,
  s: EdiSegment
): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemSegment(item: ItemLoop843, s: EdiSegment): void {
  switch (s.id) {
    case 'PID':
      item.pid.push(s);
      break;
    case 'MEA':
      item.mea.push(s);
      break;
    case 'CTP':
      item.ctp = s;
      break;
    case 'SAC':
      item.sac.push(s);
      break;
    case 'REF':
      item.refs.push(s);
      break;
    case 'PER':
      item.per = s;
      break;
    case 'SDQ':
      item.sdq.push(s);
      break;
    case 'DTM':
      item.dtm.push(s);
      break;
    default:
      item.other.push(s);
      break;
  }
}

function emptyHeader(): Header843 {
  return {refs: [], dtm: [], msg: [], other: []};
}

function emptyItem(po1: EdiSegment): ItemLoop843 {
  return {
    po1,
    pid: [],
    mea: [],
    sac: [],
    refs: [],
    sdq: [],
    dtm: [],
    subNameLoops: [],
    other: [],
  };
}

type Phase = 'header' | 'name' | 'item' | 'summary';
type ItemSubPhase = 'main' | 'name';

/**
 * Walks one 843 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is bucketed into that loop's `other` list
 * rather than the walk aborting; the validator is what reports on a
 * document that's missing or misordered.
 */
export function parse843Segments(segments: EdiSegment[]): Parsed843 {
  const header = emptyHeader();
  const nameLoops: NameLoop843[] = [];
  const items: ItemLoop843[] = [];
  const summary: Summary843 = {amt: []};

  let phase: Phase = 'header';
  let itemSubPhase: ItemSubPhase = 'main';
  let currentNameLoop: NameLoop843 | undefined;
  let currentItem: ItemLoop843 | undefined;
  let currentItemNameSubLoop: ItemNameSubLoop843 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'N1':
        if (phase === 'item' && currentItem) {
          itemSubPhase = 'name';
          currentItemNameSubLoop = {n1: s, other: []};
          currentItem.subNameLoops.push(currentItemNameSubLoop);
        } else {
          phase = 'name';
          currentNameLoop = {n1: s, n9: [], refs: [], other: []};
          nameLoops.push(currentNameLoop);
        }
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
      case 'N9':
        if (itemSubPhase === 'name' && currentItemNameSubLoop) {
          attachItemNameSubLoopSegment(currentItemNameSubLoop, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        }
        break;

      case 'PO1':
        phase = 'item';
        itemSubPhase = 'main';
        currentItemNameSubLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      case 'CTT':
        phase = 'summary';
        summary.ctt = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        currentItemNameSubLoop = undefined;
        break;

      case 'AMT':
        summary.amt.push(s);
        break;

      default:
        if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        }
        break;
    }
  }

  return {header, nameLoops, items, summary};
}

export interface EdiTransactionWithParsed843 extends EdiTransaction {
  parsed843?: Parsed843;
}

export interface EdiGroupWithParsed843 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed843[];
}

export interface EdiEnvelopeWithParsed843 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed843[];
}

export function attachParsed843(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed843 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed843:
          transaction.transactionSetCode === '843'
            ? parse843Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
